'use client';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { useHints } from '@wt/game/providers/hints/HintsProvider';
import HintBox from './HintBox';
import { SwipeableDrawer } from '@mui/material';
import { useResponsive } from '@wt/shared/hooks/useResponsive';
import { useEffect, useState } from 'react';
import { Spinner } from '@wt/shared/components/spinner/spinner';
import { getSiteMode } from '@wt/utilities/siteMode';

export default function HintWalletModal(props: { mode: 'results' | 'game' }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [availableHints, setAvailableHints] = useState<string[]>([]);
  const hints = useHints();
  const closeHintsModal = () => {
    hints.setHintsModalOpen(false);
  };

  useEffect(() => {
    if (!hints.hintsModalOpen) return;
    (async () => {
      const availableHints = await hints.getAvailableHints();
      setIsLoaded(true);
      setAvailableHints(availableHints);
    })();
  }, [hints.hintsModalOpen, hints.getAvailableHints]);

  const responsiveInfo = useResponsive();

  if (responsiveInfo['md']) {
    return (
      <Modal
        open={hints.hintsModalOpen}
        onClose={closeHintsModal}
        closeAfterTransition
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          '&.MuiBox-root': {
            border: 'none',
          },
          '&:focus': {
            outline: 'none',
          },
        }}
      >
        <Box
          sx={{
            '&:focus': {
              outline: 'none',
            },
          }}
        >
          {!isLoaded && <Spinner />}
          {isLoaded && (
            <HintWalletContent
              availableHints={availableHints}
              handleClose={closeHintsModal}
              isMobile={false}
              mode={props.mode}
            />
          )}
        </Box>
      </Modal>
    );
  }

  return (
    <SwipeableDrawer
      anchor={'bottom'}
      open={hints.hintsModalOpen}
      onClose={closeHintsModal}
      onOpen={() => {
        // do nothing
      }}
      PaperProps={{
        elevation: 0,
        style: { backgroundColor: 'transparent' },
      }}
    >
      <HintWalletContent
        availableHints={availableHints}
        handleClose={closeHintsModal}
        isMobile={true}
        mode={props.mode}
      />
    </SwipeableDrawer>
  );
}

const HintWalletContent = (props: {
  isMobile: boolean;
  availableHints: string[];
  mode: 'results' | 'game';
  handleClose: () => void;
}) => {
  const desktopClass =
    'm-4 flex flex-col rounded-3xl bg-base-100 p-4 pt-0 md:px-16 md:pb-16 text-lg w-[80vw] md:w-[580px] max-h-[80vh]';
  const mobileClass =
    'w-full h-[85vh] flex flex-col rounded-t-3xl bg-base-100 p-4 pt-0 md:px-16 md:pb-16 text-lg ';

  const locationHints = [];
  if (getSiteMode() == 'whentaken') {
    locationHints.push({
      hint: 'proximitypic',
      label: '🖼️ Close-by Capture',
      disabled: !props.availableHints.includes('proximitypic'),
    });
  }
  locationHints.push({
    hint: 'dualdrop',
    label: '⚖️ Dual Drop',
    disabled: !props.availableHints.includes('dualdrop'),
  });

  const yearHints = [];
  if (getSiteMode() == 'whentaken') {
    yearHints.push({
      hint: 'eraexposure',
      label: '📸 Era Exposure',
      disabled: !props.availableHints.includes('eraexposure'),
    });
  }
  yearHints.push({
    hint: 'timetidbit',
    label: '🧠 Timeline Trivia',
    disabled: !props.availableHints.includes('timetidbit'),
  });

  return (
    <div className={props.isMobile ? mobileClass : desktopClass}>
      <div className="w-full mt-2 md:mt-0 bg-base-100 py-2 md:pt-10 md:pb-5">
        <div className="relative text-center">
          <span className="text-xl md:text-4xl font-medium">Hint wallet</span>
          <div
            className="absolute right-0 ease-out duration-300 cursor-pointer hover:bg-secondary p-1 rounded-lg"
            style={{
              top: '50%',
              marginTop: '-22px',
            }}
            onClick={props.handleClose}
          >
            <CloseRoundedIcon className="m-0 fill-current text-4xl leading-none hidden md:block" />
            <DownRoundedIcon className="m-0 fill-current text-4xl leading-none md:hidden" />
          </div>
        </div>
      </div>

      <div className="overflow-y-auto">
        {getSiteMode() != 'whentaken-movies' && (
          <p className="mb-6 text-center text-sm">
            You can only use 1 location and 1 year hint per round
          </p>
        )}

        <HintBox
          title="📍 Location"
          type="location"
          mode={props.mode}
          hints={locationHints}
        />
        <div className="pb-6"></div>
        <HintBox
          title="🗓️ Year"
          type="year"
          mode={props.mode}
          hints={yearHints}
        />
      </div>
    </div>
  );
};
